import {DataTable} from "../DataTable/DataTable";
import {ServiceTypeEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useServiceTypeDataTableFilter} from "../ServiceTypeDataTable/useServiceTypeDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: ServiceTypeEntity[] = []

export type ServiceTypeDataTableProps = EntityDataTableProps<ServiceTypeEntity>

export const ServiceTypeDataTable = (props: ServiceTypeDataTableProps) => {
  const {
    records: serviceTypes = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useServiceTypeDataTableFilter({
    records: serviceTypes
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceTypeEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.servicetype.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record}/>
        ),
      }
    ]

    return values
  }, [filterConfig, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type ServiceTypeDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<ServiceTypeDataTableProps, 'recordsPerPageOptions'>
}

export const ServiceTypeDataTableModal = (props: ServiceTypeDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.servicetype.plural", "Service Types"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <ServiceTypeDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
